/**
 * In-memory object index of one sheet with memoised derived views.
 *
 * The UI renders up to ~40 pages, each from `objectsByPage.get(page)`: per-page arrays and the
 * connector list keep their identity until something on that page (or among the connectors)
 * actually changes, so page components can bail out with a reference check.
 */
import type { AnnotationObject, AnnotationOf } from '../../../shared/annotations';

export type ConnectorObject = AnnotationOf<'connector'>;

export interface DerivedViews {
  objectsByPage: ReadonlyMap<number, readonly AnnotationObject[]>;
  connectors: readonly ConnectorObject[];
}

const sameArray = <T>(a: readonly T[], b: readonly T[]) => a.length === b.length && a.every((x, i) => x === b[i]);

export class ObjectIndex {
  /** Every object by id (the store only puts live ones; tombstones are tolerated but never needed). */
  private all = new Map<string, AnnotationObject>();
  /** Live object ids per page (all layers). */
  private byPage = new Map<number, Set<string>>();
  /** All object ids per layer (incl. tombstones). */
  private byLayer = new Map<string, Set<string>>();
  private liveByLayer = new Map<string, number>();
  private connectorIds = new Set<string>();

  private dirtyPages = new Set<number>();
  private connectorsDirty = true;
  private zSig: string | null = null;
  private pageCache = new Map<number, readonly AnnotationObject[]>();
  private mapCache: ReadonlyMap<number, readonly AnnotationObject[]> = new Map();
  private connCache: readonly ConnectorObject[] = [];

  /** Live object (undefined for unknown or deleted ids). */
  get(id: string): AnnotationObject | undefined {
    const o = this.all.get(id);
    return o && o.deletedAt === null ? o : undefined;
  }

  /** Object incl. tombstones. */
  getAny(id: string): AnnotationObject | undefined {
    return this.all.get(id);
  }

  get size(): number {
    return this.all.size;
  }

  liveCount(layerId: string): number {
    return this.liveByLayer.get(layerId) ?? 0;
  }

  /** Objects of a layer (live only by default), in no particular order. */
  layerObjects(layerId: string, liveOnly = true): AnnotationObject[] {
    const ids = this.byLayer.get(layerId);
    if (!ids) return [];
    const out: AnnotationObject[] = [];
    for (const id of ids) {
      const o = this.all.get(id);
      if (o && (!liveOnly || o.deletedAt === null)) out.push(o);
    }
    return out;
  }

  put(obj: AnnotationObject): void {
    const prev = this.all.get(obj.id);
    if (prev === obj) return;
    if (prev) this.unindex(prev);
    this.all.set(obj.id, obj);
    this.index(obj);
  }

  remove(id: string): AnnotationObject | undefined {
    const prev = this.all.get(id);
    if (!prev) return undefined;
    this.unindex(prev);
    this.all.delete(id);
    return prev;
  }

  /** Removes every object of a layer; returns their ids. */
  removeLayer(layerId: string): string[] {
    return this.clearLayer(layerId, () => false);
  }

  /** Removes the objects of a layer except those `keep` returns true for; returns removed ids. */
  clearLayer(layerId: string, keep: (id: string) => boolean): string[] {
    const ids = this.byLayer.get(layerId);
    if (!ids) return [];
    const removed: string[] = [];
    for (const id of [...ids]) {
      if (keep(id)) continue;
      this.remove(id);
      removed.push(id);
    }
    return removed;
  }

  /** Forces a full rebuild of the views on next access (e.g. after visibility changes). */
  invalidateAll(): void {
    this.zSig = null;
  }

  /**
   * Derived views for the given z-order (layerId → z, bottom first; layers not in the map are
   * hidden). Within a layer, objects are ordered by last change (hlcMs, id), oldest first.
   */
  views(z: ReadonlyMap<string, number>): DerivedViews {
    let sig = '';
    for (const [id, n] of z) sig += `${id}:${n};`;
    const full = sig !== this.zSig;
    this.zSig = sig;

    const cmp = (a: AnnotationObject, b: AnnotationObject) =>
      z.get(a.layerId)! - z.get(b.layerId)! || a.hlcMs - b.hlcMs || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

    const pages = full ? new Set<number>([...this.byPage.keys(), ...this.pageCache.keys()]) : this.dirtyPages;
    let mapChanged = false;
    for (const p of pages) {
      const ids = this.byPage.get(p);
      const arr: AnnotationObject[] = [];
      if (ids) {
        for (const id of ids) {
          const o = this.all.get(id)!;
          if (z.has(o.layerId)) arr.push(o);
        }
        arr.sort(cmp);
      }
      const prev = this.pageCache.get(p);
      if (!arr.length) {
        if (prev) {
          this.pageCache.delete(p);
          mapChanged = true;
        }
      } else if (!prev || !sameArray(prev, arr)) {
        this.pageCache.set(p, arr);
        mapChanged = true;
      }
    }
    this.dirtyPages = new Set();
    if (mapChanged) {
      this.mapCache = new Map([...this.pageCache.entries()].sort((a, b) => a[0] - b[0]));
    }

    if (full || this.connectorsDirty) {
      const conns: ConnectorObject[] = [];
      for (const id of this.connectorIds) {
        const o = this.all.get(id);
        if (o && o.kind === 'connector' && z.has(o.layerId)) conns.push(o);
      }
      conns.sort(cmp);
      if (!sameArray(conns, this.connCache)) this.connCache = conns;
      this.connectorsDirty = false;
    }
    return { objectsByPage: this.mapCache, connectors: this.connCache };
  }

  private index(o: AnnotationObject): void {
    let ls = this.byLayer.get(o.layerId);
    if (!ls) this.byLayer.set(o.layerId, (ls = new Set()));
    ls.add(o.id);
    if (o.deletedAt !== null) return;
    this.liveByLayer.set(o.layerId, (this.liveByLayer.get(o.layerId) ?? 0) + 1);
    let ps = this.byPage.get(o.page);
    if (!ps) this.byPage.set(o.page, (ps = new Set()));
    ps.add(o.id);
    this.dirtyPages.add(o.page);
    if (o.kind === 'connector') {
      this.connectorIds.add(o.id);
      this.connectorsDirty = true;
    }
  }

  private unindex(o: AnnotationObject): void {
    const ls = this.byLayer.get(o.layerId);
    if (ls) {
      ls.delete(o.id);
      if (!ls.size) this.byLayer.delete(o.layerId);
    }
    if (o.deletedAt !== null) return;
    const n = (this.liveByLayer.get(o.layerId) ?? 1) - 1;
    if (n > 0) this.liveByLayer.set(o.layerId, n);
    else this.liveByLayer.delete(o.layerId);
    const ps = this.byPage.get(o.page);
    if (ps) {
      ps.delete(o.id);
      if (!ps.size) this.byPage.delete(o.page);
    }
    this.dirtyPages.add(o.page);
    if (o.kind === 'connector') {
      this.connectorIds.delete(o.id);
      this.connectorsDirty = true;
    }
  }
}
